import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { TextField } from '../../../../components/inputs/TextField';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Modal } from '../../../../components/Modal';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { applicantsApi } from '../applicantsApi';
import type { ApplicantScoreWeights } from '../applicantTypes';
import { sampleScoreWeights } from '../utils/sampleRecruitment';

const ID_PREFIX = 'score-weights';

/**
 * Every assessment stage's weight (%), at once — they must add up to EXACTLY 100, so one cannot
 * change alone. The running total is stated on the form, and Submit waits for it.
 *
 * **Saving re-scores every applicant's Summary at once** — the score is arithmetic over these
 * numbers, stored on no applicant — so the consequence is stated before the tick.
 */
export function ScoreWeightsModal({
  current,
  onClose,
  onSaved,
}: {
  current: ApplicantScoreWeights;
  onClose: () => void;
  onSaved: (next: ApplicantScoreWeights) => void;
}) {
  const toast = useToast();

  const [values, setValues] = useState<string[]>(current.weights.map((w) => (w.weight != null ? String(w.weight) : '')));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsed = values.map(parseWeight);
  const allValid = parsed.every((v) => v !== null);
  // Summed in hundredths, so 33.33 + 33.33 + 33.34 is exactly 100 rather than 99.99999.
  const totalHundredths = parsed.reduce<number>((sum, v) => sum + Math.round((v ?? 0) * 100), 0);
  const totalsHundred = totalHundredths === 10000;
  const canSave = allValid && totalsHundred && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const next = await applicantsApi.saveScoreWeights({
        weights: current.weights.map((w, i) => ({ stage: w.stage, weight: parsed[i] })),
      });

      toast.success('Saved the score points. Every applicant’s Summary is scored against them now.');
      onSaved(next);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the score points.', 0);
      setError(failure);
      // The total names no one input; the first weight stands for it.
      focusValidationError(failure, { idPrefix: ID_PREFIX, fieldIds: { weights: `${ID_PREFIX}-weights.0.weight` } });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit score points"
      size="lg"
      id={`${ID_PREFIX}-modal`}
      autogenerate={{
        onGenerate: () => setValues(sampleScoreWeights(current.weights.length).map(String)),
        disabled: saving,
      }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <p className="text-xs text-slate-500">
          Every stage is required. A Passed stage earns its weight, a Failed one nothing, and the five add up to 100.
        </p>

        {/*
          A LIST OF ONE REPEATED QUESTION — the Table Display View's one form exception (product call,
          20 Oct 2026): the stage is the tinted LEFT cell, the shared input the right, `labelHidden`
          handing its name to the cell while keeping the error wiring and the `sr-only` description.
          The explanation all five rows share is said once above, not five times in help icons.
        */}
        <PreviewTable
          variant="table-display"
          rows={current.weights.map((w, i) => ({
            label: `${w.stageLabel} (%)`,
            value: (
              <TextField
                labelHidden
                id={`${ID_PREFIX}-weights.${i}.weight`}
                // The SAME string as the cell — the announced name must contain the visible label.
                label={`${w.stageLabel} (%)`}
                required
                type="number"
                inputMode="decimal"
                min={0}
                max={100}
                step="0.01"
                className="font-mono"
                description={`The points a Passed ${w.stageLabel.toLowerCase()} earns, out of 100. Failed earns none.`}
                value={values[i]}
                onChange={(e) => setValues((current) => current.map((v, j) => (j === i ? e.target.value : v)))}
                disabled={saving}
                error={
                  values[i].trim() !== '' && parsed[i] === null
                    ? 'Give a number between 0 and 100, with at most two decimals.'
                    : error?.fieldError(`weights.${i}.weight`)
                }
              />
            ),
          }))}
        />

        <p className={`text-xs ${allValid && !totalsHundred ? 'text-red-700' : 'text-slate-600'}`} aria-live="polite">
          Total <span className="font-mono tabular-nums">{(totalHundredths / 100).toFixed(2)}</span> of{' '}
          <span className="font-mono tabular-nums">100.00</span>
          {allValid && !totalsHundred
            ? ` — ${totalHundredths < 10000 ? 'add' : 'take off'} ${(Math.abs(10000 - totalHundredths) / 100).toFixed(2)} before submitting.`
            : '.'}{' '}
          Every applicant&apos;s Summary is re-scored against these the moment you submit.
        </p>
      </div>
    </Modal>
  );
}

function parseWeight(raw: string): number | null {
  if (raw.trim() === '') return null;

  const value = Number(raw);

  // Two decimals at most — compared with a tolerance, because 33.33 * 100 is 3332.9999… in binary.
  const twoDecimals = Math.abs(Math.round(value * 100) - value * 100) < 1e-6;

  return Number.isFinite(value) && value >= 0 && value <= 100 && twoDecimals ? value : null;
}
